import React, { createContext, useEffect, useReducer, useRef } from "react";

const STORAGE_KEYS = {
  token: ["authToken", "token", "trading_app_token"],
  user: ["userData", "trading_app_user"],
};

const AUTH_STORAGE_MODE_KEY = "gainwise_auth_storage_mode";
const STORAGE_AREAS = [
  {
    name: "local",
    storage: typeof window !== "undefined" ? window.localStorage : null,
  },
  {
    name: "session",
    storage: typeof window !== "undefined" ? window.sessionStorage : null,
  },
];

const AUTH_API_BASE_URL = (
  import.meta.env.VITE_AUTH_API_BASE_URL ||
  import.meta.env.VITE_BACKEND_API_BASE_URL ||
  "https://gainwisefx.com/api/"
).replace(/\/+$/, "");

const AuthContext = createContext();

const AUTH_ACTIONS = {
  LOGIN_START: "LOGIN_START",
  LOGIN_SUCCESS: "LOGIN_SUCCESS",
  LOGIN_FAILURE: "LOGIN_FAILURE",
  LOGOUT: "LOGOUT",
  REGISTER_START: "REGISTER_START",
  REGISTER_SUCCESS: "REGISTER_SUCCESS",
  REGISTER_FAILURE: "REGISTER_FAILURE",
  UPDATE_USER: "UPDATE_USER",
  VERIFY_EMAIL_START: "VERIFY_EMAIL_START",
  VERIFY_EMAIL_SUCCESS: "VERIFY_EMAIL_SUCCESS",
  VERIFY_EMAIL_FAILURE: "VERIFY_EMAIL_FAILURE",
  RESET_PASSWORD_START: "RESET_PASSWORD_START",
  RESET_PASSWORD_SUCCESS: "RESET_PASSWORD_SUCCESS",
  RESET_PASSWORD_FAILURE: "RESET_PASSWORD_FAILURE",
  CLEAR_ERROR: "CLEAR_ERROR",
  SET_LOADING: "SET_LOADING",
  INIT_AUTH: "INIT_AUTH",
};

const normalizeUser = (user = {}) => {
  const firstName = user.firstName || user.name || "";
  const lastName = user.lastName || "";
  const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();

  return {
    ...user,
    firstName,
    lastName,
    email: user.email || "",
    name: fullName || firstName || user.email || "Trader",
  };
};

const getStorageByMode = (mode) =>
  STORAGE_AREAS.find((area) => area.name === mode)?.storage || null;

const parseStoredUser = (value) => {
  if (!value) {
    return null;
  }

  try {
    return normalizeUser(JSON.parse(value));
  } catch {
    return null;
  }
};

const clearAuthStorageArea = (storage) => {
  if (!storage) {
    return;
  }

  [...STORAGE_KEYS.token, ...STORAGE_KEYS.user].forEach((key) =>
    storage.removeItem(key)
  );
  storage.removeItem("wisefirstName");
  storage.removeItem(AUTH_STORAGE_MODE_KEY);
};

const getStoredAuthSnapshot = () => {
  let foundPartialAuth = false;

  for (const area of STORAGE_AREAS) {
    if (!area.storage) {
      continue;
    }

    const token = STORAGE_KEYS.token
      .map((key) => area.storage.getItem(key))
      .find(Boolean);
    const userRaw = STORAGE_KEYS.user
      .map((key) => area.storage.getItem(key))
      .find(Boolean);
    const user = parseStoredUser(userRaw);

    if ((token && !user) || (!token && user)) {
      foundPartialAuth = true;
      continue;
    }

    if (token && user) {
      return {
        token,
        user,
        storageMode: area.name,
      };
    }
  }

  if (foundPartialAuth) {
    STORAGE_AREAS.forEach((area) => clearAuthStorageArea(area.storage));
  }

  return {
    token: null,
    user: null,
    storageMode: "local",
  };
};

const createAuthHeaders = (token) => {
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
    headers["X-Auth-Token"] = token;
  }

  return headers;
};

const extractErrorMessage = (payload, fallback) => {
  if (!payload) {
    return fallback;
  }

  if (typeof payload === "string") {
    return payload;
  }

  return payload.message || payload.error || fallback;
};

const requestAuthJson = async (endpoint, options = {}) => {
  const response = await fetch(`${AUTH_API_BASE_URL}/${endpoint}`, options);
  const payload = await response.json().catch(() => null);

  if (!response.ok || payload?.success === false) {
    const error = new Error(
      extractErrorMessage(payload, "Authentication request failed.")
    );
    error.status = response.status;
    throw error;
  }

  return payload;
};

const clearStoredAuth = () => {
  try {
    STORAGE_AREAS.forEach((area) => clearAuthStorageArea(area.storage));
  } catch (error) {
    console.warn("Failed to clear auth data from storage:", error);
  }
};

const clearFrontendSessionState = () => {
  clearStoredAuth();

  try {
    STORAGE_AREAS.forEach((area) => {
      const storage = area.storage;
      if (!storage) {
        return;
      }

      const keysToRemove = [];
      for (let index = 0; index < storage.length; index += 1) {
        const key = storage.key(index);
        if (!key) {
          continue;
        }

        if (
          key === "gainwise-trade-live-state-v2" ||
          key.startsWith("gainwise-trade-live-state-v2:") ||
          key.startsWith("gainwise-trading-state-v3:")
        ) {
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach((key) => storage.removeItem(key));
    });
  } catch (error) {
    console.warn("Failed to clear frontend session state:", error);
  }
};

const storeAuth = (user, token, storageMode = "local") => {
  if (!user || !token) {
    clearStoredAuth();
    return;
  }

  const normalizedUser = normalizeUser(user);
  const targetStorage = getStorageByMode(storageMode) || getStorageByMode("local");

  try {
    STORAGE_AREAS.forEach((area) => {
      if (!area.storage || area.storage === targetStorage) {
        return;
      }

      [...STORAGE_KEYS.token, ...STORAGE_KEYS.user].forEach((key) =>
        area.storage.removeItem(key)
      );
      area.storage.removeItem("wisefirstName");
      area.storage.removeItem(AUTH_STORAGE_MODE_KEY);
    });

    targetStorage.setItem("authToken", token);
    targetStorage.setItem("token", token);
    targetStorage.setItem("trading_app_token", token);
    targetStorage.setItem("userData", JSON.stringify(normalizedUser));
    targetStorage.setItem("trading_app_user", JSON.stringify(normalizedUser));
    targetStorage.setItem("wisefirstName", normalizedUser.firstName || "");
    targetStorage.setItem(AUTH_STORAGE_MODE_KEY, storageMode);
  } catch (error) {
    console.warn("Failed to sync auth data to storage:", error);
  }
};

const initialAuthSnapshot = getStoredAuthSnapshot();

const initialState = {
  user: initialAuthSnapshot.user,
  token: initialAuthSnapshot.token,
  isAuthenticated: !!(initialAuthSnapshot.token && initialAuthSnapshot.user),
  isLoading: true,
  isLoginLoading: false,
  isRegisterLoading: false,
  isVerifyingEmail: false,
  isResettingPassword: false,
  error: null,
  message: null,
};

const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.INIT_AUTH:
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: action.payload.isAuthenticated,
        isLoading: false,
      };

    case AUTH_ACTIONS.LOGIN_START:
      return {
        ...state,
        isLoginLoading: true,
        error: null,
        message: null,
      };

    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoginLoading: false,
        isLoading: false,
        error: null,
        message: action.payload.message || "Login successful",
      };

    case AUTH_ACTIONS.LOGIN_FAILURE:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoginLoading: false,
        isLoading: false,
        error: action.payload,
      };

    case AUTH_ACTIONS.REGISTER_START:
      return {
        ...state,
        isRegisterLoading: true,
        error: null,
        message: null,
      };

    case AUTH_ACTIONS.REGISTER_SUCCESS:
      return {
        ...state,
        isRegisterLoading: false,
        error: null,
        message: action.payload.message || "Registration successful",
      };

    case AUTH_ACTIONS.REGISTER_FAILURE:
      return {
        ...state,
        isRegisterLoading: false,
        error: action.payload,
      };

    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        message: null,
      };

    case AUTH_ACTIONS.UPDATE_USER:
      return {
        ...state,
        user: { ...state.user, ...action.payload },
        message: "Profile updated successfully",
      };

    case AUTH_ACTIONS.VERIFY_EMAIL_START:
      return {
        ...state,
        isVerifyingEmail: true,
        error: null,
        message: null,
      };

    case AUTH_ACTIONS.VERIFY_EMAIL_SUCCESS:
      return {
        ...state,
        isVerifyingEmail: false,
        user: state.user ? { ...state.user, emailVerified: true } : null,
        message: action.payload.message || "Email verified successfully",
      };

    case AUTH_ACTIONS.VERIFY_EMAIL_FAILURE:
      return {
        ...state,
        isVerifyingEmail: false,
        error: action.payload,
      };

    case AUTH_ACTIONS.RESET_PASSWORD_START:
      return {
        ...state,
        isResettingPassword: true,
        error: null,
        message: null,
      };

    case AUTH_ACTIONS.RESET_PASSWORD_SUCCESS:
      return {
        ...state,
        isResettingPassword: false,
        message: action.payload.message || "Password reset email sent",
      };

    case AUTH_ACTIONS.RESET_PASSWORD_FAILURE:
      return {
        ...state,
        isResettingPassword: false,
        error: action.payload,
      };

    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
        message: null,
      };

    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      };

    default:
      return state;
  }
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const storageModeRef = useRef(initialAuthSnapshot.storageMode || "local");

  useEffect(() => {
    const initializeAuth = async () => {
      const snapshot = getStoredAuthSnapshot();
      storageModeRef.current = snapshot.storageMode || "local";

        if (!snapshot.token || !snapshot.user) {
        clearFrontendSessionState();
        dispatch({
          type: AUTH_ACTIONS.INIT_AUTH,
          payload: {
            user: null,
            token: null,
            isAuthenticated: false,
          },
        });
        return;
      }

      try {
        const response = await requestAuthJson("get-user-details.php", {
          method: "GET",
          headers: createAuthHeaders(snapshot.token),
        });

        const refreshedUser = normalizeUser(response.user || snapshot.user);
        storeAuth(refreshedUser, snapshot.token, storageModeRef.current);

        dispatch({
          type: AUTH_ACTIONS.INIT_AUTH,
          payload: {
            user: refreshedUser,
            token: snapshot.token,
            isAuthenticated: true,
          },
        });
      } catch (error) {
        if (error?.status === 401 || error?.status === 403 || error?.status === 404) {
          clearFrontendSessionState();
          dispatch({
            type: AUTH_ACTIONS.INIT_AUTH,
            payload: {
              user: null,
              token: null,
              isAuthenticated: false,
            },
          });
          return;
        }

        dispatch({
          type: AUTH_ACTIONS.INIT_AUTH,
          payload: {
            user: snapshot.user,
            token: snapshot.token,
            isAuthenticated: true,
          },
        });
      }
    };

    initializeAuth();
  }, []);

  useEffect(() => {
    const handleStorageChange = () => {
      const snapshot = getStoredAuthSnapshot();
      const hasValidSnapshot = !!(snapshot.token && snapshot.user);

      if (!hasValidSnapshot) {
        clearFrontendSessionState();
      }

      dispatch({
        type: AUTH_ACTIONS.INIT_AUTH,
        payload: {
          user: hasValidSnapshot ? snapshot.user : null,
          token: hasValidSnapshot ? snapshot.token : null,
          isAuthenticated: hasValidSnapshot,
        },
      });
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  useEffect(() => {
    const handleForcedLogout = () => {
      clearFrontendSessionState();
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
    };

    window.addEventListener("auth:logout", handleForcedLogout);
    return () => window.removeEventListener("auth:logout", handleForcedLogout);
  }, []);

  useEffect(() => {
    storeAuth(state.user, state.token, storageModeRef.current);
  }, [state.user, state.token]);

  const login = async (email, password, options = {}) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });

    try {
      storageModeRef.current = options.rememberMe === false ? "session" : "local";

      const response = await requestAuthJson("signin.php", {
        method: "POST",
        headers: createAuthHeaders(),
        body: JSON.stringify({
          email: email.trim(),
          password,
        }),
      });

      const user = normalizeUser(response.user);
      const token = response.token;
      storeAuth(user, token, storageModeRef.current);

      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: {
          user,
          token,
          message: response.message || "Login successful",
        },
      });

      return { success: true, data: { user, token } };
    } catch (error) {
      const errorMessage = error.message || "Login failed. Please try again.";

      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: errorMessage,
      });

      return { success: false, error: errorMessage };
    }
  };

  const register = async (userData) => {
    dispatch({ type: AUTH_ACTIONS.REGISTER_START });

    try {
      const registrationPayload = {
        firstName: userData.firstName?.trim() || "",
        lastName: userData.lastName?.trim() || "",
        email: userData.email?.trim() || "",
        password: userData.password,
        phone: userData.phone?.trim() || "",
        country: userData.country?.trim() || "",
        agreeToMarketing: !!userData.agreeToMarketing,
      };

      const response = await requestAuthJson("signup.php", {
        method: "POST",
        headers: createAuthHeaders(),
        body: JSON.stringify(registrationPayload),
      });

      let loginResult = null;

      if (response?.token && response?.user) {
        storageModeRef.current = "local";
        const registeredUser = normalizeUser(response.user);
        const registeredToken = response.token;
        storeAuth(registeredUser, registeredToken, storageModeRef.current);

        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: {
            user: registeredUser,
            token: registeredToken,
            message: response.message || "Registration successful",
          },
        });

        loginResult = {
          success: true,
          data: {
            user: registeredUser,
            token: registeredToken,
          },
        };
      } else {
        loginResult = await login(
          registrationPayload.email,
          registrationPayload.password,
          { rememberMe: true }
        );
      }

      if (!loginResult.success) {
        throw new Error(loginResult.error || "Login after registration failed.");
      }

      dispatch({
        type: AUTH_ACTIONS.REGISTER_SUCCESS,
        payload: {
          message: "Registration successful",
        },
      });

      return {
        success: true,
        data: {
          message: "Registration successful",
          user: loginResult.data.user,
          token: loginResult.data.token,
        },
      };
    } catch (error) {
      const errorMessage = error.message || "Registration failed. Please try again.";

      dispatch({
        type: AUTH_ACTIONS.REGISTER_FAILURE,
        payload: errorMessage,
      });

      return { success: false, error: errorMessage };
    }
  };

  const logout = async () => {
    clearFrontendSessionState();
    dispatch({ type: AUTH_ACTIONS.LOGOUT });
  };

  const updateUser = async (userData) => {
    try {
      const response = await requestAuthJson("update-profile.php", {
        method: "POST",
        headers: createAuthHeaders(state.token),
        body: JSON.stringify(userData),
      });

      const updatedUser = normalizeUser({ ...state.user, ...response.user });

      dispatch({
        type: AUTH_ACTIONS.UPDATE_USER,
        payload: updatedUser,
      });

      return { success: true, data: { user: updatedUser } };
    } catch (error) {
      const errorMessage = error.message || "Profile update failed.";
      return { success: false, error: errorMessage };
    }
  };

  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  };

  const hasPermission = (permission) => {
    if (!state.user) {
      return false;
    }

    return (
      state.user.permissions?.includes(permission) ||
      state.user.role === "admin"
    );
  };

  const value = {
    user: state.user,
    token: state.token,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    isLoginLoading: state.isLoginLoading,
    isRegisterLoading: state.isRegisterLoading,
    isVerifyingEmail: state.isVerifyingEmail,
    isResettingPassword: state.isResettingPassword,
    error: state.error,
    message: state.message,
    loading: state.isLoading,
    login,
    register,
    logout,
    updateUser,
    clearError,
    hasPermission,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export { AuthContext };

export default AuthContext;
